import { Card, Checkbox, Text, Badge, Group, Divider, Grid, Box, Flex, Button } from '@mantine/core';
import { IconCalendar, IconPackage, IconScale, IconBox, IconEye } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { Package } from './PackageList';

interface PackageItemProps {
    pkg: any;
    isSelected: boolean;
    onPackageSelect: (id: string, pkg: Package) => void;
    canSelectPackage: (pkg: Package) => boolean;
    isMobile: boolean;
}

const PackageItem = ({
    pkg,
    isSelected,
    onPackageSelect,
    canSelectPackage,
    isMobile,
}: PackageItemProps) => {
    const navigate = useNavigate();

    // Helper functions
    const formatDate = (dateString: string) => {
        return dateString ? new Date(dateString).toLocaleDateString() : 'N/A';
    };

    // Normalize package data structure
    const normalizePackageData = (pkg: any) => {
        return {
            id: pkg.id,
            description: pkg.description,
            weight: pkg.weight,
            dimensions: pkg.dimensions || {
                length: pkg.length,
                width: pkg.width,
                height: pkg.height
            },
            customValue: pkg.customValue || pkg.custom_value,
            status: pkg.status,
            receivedDate: pkg.receivedDate || pkg.created_at,
            type: pkg.type,
            item: pkg.item || pkg.package || [], // For combined packages, items are in 'package' field
            shipment: pkg.shipment,
            combinedPackage: pkg.combinedPackage,
            isDraft: pkg.isDraft,
            quantity: pkg.quantity
        };
    };

    const normalizedPkg: any = normalizePackageData(pkg);

    const getPackageName = (pkg: any) => {
        if (pkg.description && pkg.description.trim() !== '') {
            return pkg.description;
        } else if (pkg.item && pkg.item.length > 0) {
            return pkg.item.map((item: any) => item.name || item.description).join(', ').substring(0, 30) +
                (pkg.item.length > 1 ? '...' : '');
        } else {
            return `Package #${pkg.id}`;
        }
    };

    // Check if package has a shipment with REQUESTED state
    const isShipmentRequested = normalizedPkg.shipment && normalizedPkg.shipment.state === "REQUESTED";

    // Get display status for the badge
    const getDisplayStatus = (pkg: any) => {
        if (isShipmentRequested) {
            return 'Req to Ship';
        }

        // Handle different status formats
        if (pkg.type === 'combined') {
            return 'CONSOLIDATED';
        } else if (pkg.type === 'shipped' || pkg.status === 'SHIPPED') {
            return 'SHIPPED';
        } else if (pkg.status === 'RECEIVED') {
            return 'RECEIVED';
        }

        return pkg.status || 'N/A';
    };

    const getStatusColor = (status: string) => {
        if (isShipmentRequested) {
            return 'orange'; // Use orange color for requested shipments
        }

        const normalizedStatus = status?.toUpperCase();
        switch (normalizedStatus) {
            case 'SHIPPED':
                return 'indigo';
            case 'RECEIVED':
                return 'green';
            case 'CONSOLIDATED':
                return 'blue';
            default:
                return 'gray';
        }
    };

    // Determine the correct navigation URL based on package status and type
    const getNavigationUrl = (pkg: any) => {
        // If package has shipment data, navigate to shipment page
        if (pkg.shipment && pkg.shipment.id) {
            return `/shipments/${pkg.shipment.id}`;
        }

        // Determine package type based on the package data
        let packageType = 'received'; // default
        if (pkg.type === 'combined' || pkg.combinedPackage) {
            packageType = 'consolidated';
        } else if (pkg.type === 'shipped' || pkg.status === 'SHIPPED') {
            packageType = 'shipped';
        }

        // Navigate to package detail with type parameter
        return `/packages/${pkg.id}?type=${packageType}`;
    };

    const handleCardClick = (e: React.MouseEvent) => {
        // Prevent navigation when clicking on checkbox
        if ((e.target as HTMLElement).closest('.checkbox-container')) return;

        // Prevent navigation for packages with requested shipment
        if (isShipmentRequested) return;

        navigate(getNavigationUrl(normalizedPkg));
    };

    // Render approval waiting message for requested shipments
    const renderApprovalHint = () => {
        if (!isShipmentRequested) return null;

        return (
            <Flex align="center" gap="xs" mt={6}>
                <IconCalendar size="0.8rem" color="orange" />
                <Text size="xs" c="orange" >
                    Waiting for approval
                </Text>
            </Flex>
        );
    };

    // Get item count for display
    const getItemCount = (pkg: any) => {
        if (pkg.type === 'combined' && pkg.package) {
            return pkg.package.length;
        }
        return pkg.item?.length || 0;
    };

    // Get weight display
    const getWeightDisplay = (pkg: any) => {
        return pkg.weight ? `${pkg.weight} kg` : 'N/A';
    };

    // Get dimensions display
    const getDimensionsDisplay = (pkg: any) => {
        const dims = pkg.dimensions;
        if (dims && typeof dims === 'object') {
            return `${dims.length || 0} × ${dims.width || 0} × ${dims.height || 0} cm`;
        }
        return 'N/A';
    };

    // Get custom value display
    const getCustomValueDisplay = (pkg: any) => {
        const value = pkg.customValue || pkg.custom_value;
        return value ? `$${value}` : 'N/A';
    };

    // Mobile compact view
    if (isMobile) {
        const isShipped = !canSelectPackage(normalizedPkg) || isShipmentRequested;

        return (
            <Card
                shadow="sm"
                p="xs"
                radius="md"
                withBorder
                onClick={handleCardClick}
                styles={(theme) => ({
                    root: {
                        cursor: isShipmentRequested ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        transform: 'translateY(0)',
                        opacity: isShipped ? 0.8 : 1,
                        backgroundColor: isShipped ?
                            (theme.black === 'black' ? theme.colors.dark[6] : theme.colors.gray[0]) :
                            undefined,
                        borderLeft: isShipped ?
                            `3px solid ${theme.colors[getStatusColor(getDisplayStatus(normalizedPkg))][5]}` :
                            undefined,
                        '&:hover': {
                            boxShadow: theme.shadows.md,
                            transform: 'translateY(-2px)',
                            backgroundColor: theme.black === 'black'
                                ? (isShipped ? theme.colors.dark[6] : theme.colors.dark[5])
                                : (isShipped ? theme.colors.gray[1] : theme.colors.gray[0])
                        },
                        '&:active': {
                            transform: 'translateY(0)',
                            boxShadow: theme.shadows.sm,
                            backgroundColor: theme.black === 'black'
                                ? (isShipped ? theme.colors.dark[7] : theme.colors.dark[7])
                                : (isShipped ? theme.colors.gray[2] : theme.colors.gray[1])
                        }
                    }
                })}
            >
                <Flex align="center" justify="space-between" mb={6}>
                    <Flex align="center" className="checkbox-container" onClick={e => e.stopPropagation()} style={{ flex: 1 }}>
                        <Checkbox
                            checked={isSelected}
                            onChange={() => onPackageSelect(normalizedPkg.id.toString(), normalizedPkg)}
                            size="md"
                            radius="sm"
                            disabled={!canSelectPackage(normalizedPkg) || !!isShipmentRequested}
                            title={!canSelectPackage(normalizedPkg) || isShipmentRequested ?
                                "Packages with requested or completed shipments cannot be selected" : ""}
                        />
                        <Text fw={600} size="sm" lineClamp={1} ml="xs" style={{ flex: 1 }}>
                            {getPackageName(normalizedPkg)}
                        </Text>
                    </Flex>
                    <Badge color={getStatusColor(getDisplayStatus(normalizedPkg))} size="sm" style={{ flexShrink: 0 }}>
                        {getDisplayStatus(normalizedPkg)}
                    </Badge>
                </Flex>

                <Flex gap="md" mt={8}>
                    {normalizedPkg.receivedDate && (
                        <Text size="xs" c="dimmed" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <IconCalendar size="0.9rem" />
                            {formatDate(normalizedPkg.receivedDate)}
                        </Text>
                    )}

                    <Text size="xs" c="dimmed" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <IconScale size="0.9rem" />
                        {getWeightDisplay(normalizedPkg)}
                    </Text>
                </Flex>

                {renderApprovalHint()}
            </Card>
        );
    }

    // Desktop view (original implementation)
    return (
        <Card
            shadow="sm"
            p="md"
            radius="md"
            withBorder
            styles={(theme) => ({
                root: {
                    cursor: isShipmentRequested ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: isShipmentRequested ? 0.8 : 1,
                    '&:hover': {
                        boxShadow: theme.shadows.md,
                        transform: isShipmentRequested ? 'none' : 'translateY(-2px)',
                        backgroundColor: theme.black === 'black'
                            ? (isShipmentRequested ? theme.colors.dark[6] : theme.colors.dark[5])
                            : (isShipmentRequested ? theme.colors.gray[1] : theme.colors.gray[0])
                    },
                    '&:active': {
                        transform: 'translateY(0)',
                        boxShadow: theme.shadows.sm,
                        backgroundColor: theme.black === 'black'
                            ? (isShipmentRequested ? theme.colors.dark[7] : theme.colors.dark[7])
                            : (isShipmentRequested ? theme.colors.gray[2] : theme.colors.gray[1])
                    }
                }
            })}
        >
            <Flex direction="row" align="flex-start" gap="md">
                <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <Checkbox
                        checked={isSelected}
                        onChange={() => onPackageSelect(normalizedPkg.id.toString(), normalizedPkg)}
                        size="md"
                        radius="sm"
                        mr="md"
                        disabled={!canSelectPackage(normalizedPkg) || !!isShipmentRequested}
                        title={!canSelectPackage(normalizedPkg) || isShipmentRequested ?
                            "Packages with requested or completed shipments cannot be selected" : ""}
                    />
                </Box>

                <Box onClick={handleCardClick} style={{ flex: 1 }}>
                    <Group>
                        <Text fw={700} size="md" lineClamp={1}>{getPackageName(normalizedPkg)}</Text>
                        <Badge color={getStatusColor(getDisplayStatus(normalizedPkg))} size="sm">
                            {getDisplayStatus(normalizedPkg)}
                        </Badge>
                    </Group>

                    <Group mt={4}>
                        {normalizedPkg.receivedDate && (
                            <Text size="xs" c="dimmed" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <IconCalendar size="0.9rem" />
                                {formatDate(normalizedPkg.receivedDate)}
                            </Text>
                        )}

                        <Text size="xs" c="dimmed" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <IconPackage size="0.9rem" />
                            {getItemCount(normalizedPkg)} items
                        </Text>
                    </Group>

                    {renderApprovalHint()}

                    <Divider my="xs" />

                    <Grid gutter="xs">
                        <Grid.Col span={4}>
                            <Text size="xs" fw={500} c="dimmed" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <IconScale size="0.9rem" />
                                WEIGHT
                            </Text>
                            <Text size="sm">{getWeightDisplay(normalizedPkg)}</Text>
                        </Grid.Col>

                        <Grid.Col span={4}>
                            <Text size="xs" fw={500} c="dimmed" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <IconBox size="0.9rem" />
                                DIMENSIONS
                            </Text>
                            <Text size="sm">{getDimensionsDisplay(normalizedPkg)}</Text>
                        </Grid.Col>

                        <Grid.Col span={4}>
                            <Text size="xs" fw={500} c="dimmed">VALUE</Text>
                            <Text size="sm">{getCustomValueDisplay(normalizedPkg)}</Text>
                        </Grid.Col>
                    </Grid>

                    <Group justify="flex-end" mt="md">
                        <Button
                            variant="light"
                            size="xs"
                            leftSection={<IconEye size={14} />}
                            onClick={handleCardClick}
                            disabled={isShipmentRequested}
                            style={{ opacity: isShipmentRequested ? 0.6 : 1 }}
                        >
                            View Details
                        </Button>
                    </Group>
                </Box>
            </Flex>
        </Card>
    );
};

export default PackageItem;
